import type { CSSProperties, PropsWithChildren } from "react";

import { useTheme } from "components/products/muggleTestV0/components/theme-provider";

/**
 * Landing-page theme scope.
 *
 * CSS variable values use the same "H S% L%" channel format as globals.css
 * so that Tailwind's `hsl(var(--xxx))` wrapper works for every utility,
 * including opacity modifiers, hover variants, gradients, and ring states.
 */
const lightLandingTheme = {
  /* warm off-white background */
  "--background": "40 33% 98%",
  /* near-black foreground */
  "--foreground": "265 20% 15%",

  "--card": "0 0% 100%",
  "--card-foreground": "265 20% 15%",

  "--popover": "0 0% 100%",
  "--popover-foreground": "265 20% 15%",

  "--primary": "265 20% 15%",
  "--primary-foreground": "40 33% 98%",

  "--secondary": "40 11% 94%",
  "--secondary-foreground": "265 20% 15%",

  "--muted": "40 11% 94%",
  "--muted-foreground": "265 5% 45%",

  /* warm orange accent */
  "--accent": "25 80% 50%",
  "--accent-foreground": "40 33% 98%",

  /* red destructive */
  "--destructive": "10 75% 45%",
  "--destructive-foreground": "40 33% 98%",

  "--border": "40 8% 90%",
  "--input": "40 8% 90%",
  "--ring": "25 80% 50%",
  "--radius": "0.5rem",
} as CSSProperties;

/**
 * Dark mode token overrides for the landing page theme scope.
 */
const darkLandingTheme = {
  "--background": "260 17% 13%",
  "--foreground": "40 33% 96%",

  "--card": "260 16% 17%",
  "--card-foreground": "40 33% 96%",

  "--popover": "260 16% 17%",
  "--popover-foreground": "40 33% 96%",

  "--primary": "40 33% 96%",
  "--primary-foreground": "260 17% 13%",

  "--secondary": "260 10% 22%",
  "--secondary-foreground": "40 33% 96%",

  "--muted": "260 10% 22%",
  "--muted-foreground": "35 12% 72%",

  "--accent": "25 85% 58%",
  "--accent-foreground": "260 17% 13%",

  "--destructive": "10 70% 55%",
  "--destructive-foreground": "40 33% 96%",

  "--border": "260 10% 28%",
  "--input": "260 10% 28%",
  "--ring": "25 85% 58%",
  "--radius": "0.5rem",
} as CSSProperties;

export function LandingThemeScope({ children }: PropsWithChildren) {
  const { resolvedTheme } = useTheme();

  const activeTheme = resolvedTheme === "dark" ? darkLandingTheme : lightLandingTheme;

  return (
    <div
      className="flex min-h-screen flex-col bg-background text-foreground font-sans antialiased"
      style={activeTheme}
    >
      {children}
    </div>
  );
}
